/**
 * Standard branded Excel export — a real .xlsx (via exceljs), not the legacy HTML-table-as-.xls
 * trick previously duplicated across four feature folders (inventory/procurement/assets/
 * requisitions). Every report export in the app should route through this so the letterhead
 * (logo + company name/address/contact + generated-by stamp) is identical everywhere.
 *
 * Why exceljs over the old HTML trick: a genuine xlsx binary lets us anchor the logo image to
 * a cell properly, set real column widths, and merge cells for the letterhead block — the HTML
 * trick technically works for plain tables but image embedding in it is unreliable across Excel
 * versions.
 */
import ExcelJS from "exceljs";
import { getLogoDataUrl, getReportLetterhead, getGeneratedStamp, LOGO_ASPECT } from "./reportBranding";

export type ExportRow = (string | number | null | undefined)[];

export type XlsxExportOptions = {
  /** Used as both the workbook filename and the worksheet name (truncated to Excel's 31-char limit). */
  title: string;
  headers: string[];
  rows: ExportRow[];
  filename: string;
  /** Optional extra line under the report title — e.g. active filters, record count. */
  subtitle?: string;
  /** Display name/email of whoever ran the export — shown in the generated-by stamp. */
  generatedBy?: string | null;
};

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const HEADER_FILL = "FF047857";   // emerald-700
const ALT_ROW_FILL = "FFF8FAFC";  // slate-50
const BORDER_COLOR = "FFD1D5DB";  // slate-300


// ─────────────────────────────────────────────────────────────────────────────
// Sectioned export — one sheet per section (e.g. one leave type's employee list),
// each carrying its own copy of the letterhead + a section heading band. Used where a single
// flat table would be too wide/unreadable (e.g. the Leave Balance Matrix, which used to cram
// every leave type into one table with 4 columns each — see LeaveReportsPage.tsx).
// ─────────────────────────────────────────────────────────────────────────────

export type XlsxSection = {
  /** Also used (truncated to 31 chars, de-duplicated) as the sheet's tab name. */
  heading: string;
  headers: string[];
  rows: ExportRow[];
};

export type SectionedXlsxExportOptions = {
  title: string;
  sections: XlsxSection[];
  filename: string;
  subtitle?: string;
  generatedBy?: string | null;
};

export async function exportSectionsToXlsx({
  title,
  sections,
  filename,
  subtitle,
  generatedBy,
}: SectionedXlsxExportOptions): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "EATTA Electronic Management System";
  workbook.created = new Date();

  const letterhead = getReportLetterhead();
  const usedSheetNames = new Set<string>();

  for (const section of sections) {
    let sheetName = (section.heading || "Sheet").slice(0, 31);
    let suffix = 2;
    while (usedSheetNames.has(sheetName)) {
      sheetName = `${section.heading.slice(0, 27)} (${suffix})`;
      suffix += 1;
    }
    usedSheetNames.add(sheetName);

    const sheet = workbook.addWorksheet(sheetName);
    const colCount = Math.max(section.headers.length, 4);
    const lastColLetter = sheet.getColumn(colCount).letter;

    // ── Letterhead block (rows 1–3): logo top-left, company identity to its right ─────────
    sheet.mergeCells(`B1:${lastColLetter}1`);
    sheet.getCell("B1").value = letterhead.companyName;
    sheet.getCell("B1").font = { bold: true, size: 14, color: { argb: "FF047857" } };

    sheet.mergeCells(`B2:${lastColLetter}2`);
    sheet.getCell("B2").value = letterhead.addressLine;
    sheet.getCell("B2").font = { size: 9, color: { argb: "FF64748B" } };

    sheet.mergeCells(`B3:${lastColLetter}3`);
    sheet.getCell("B3").value = letterhead.contactLine;
    sheet.getCell("B3").font = { size: 9, color: { argb: "FF64748B" } };

    try {
      const dataUrl = await getLogoDataUrl();
      const base64 = dataUrl.split(",")[1];
      const imageId = workbook.addImage({ base64, extension: "png" });
      const logoHeightRows = 4;
      const logoHeightPx = logoHeightRows * 20;
      sheet.addImage(imageId, {
        tl: { col: 0, row: 0 },
        ext: { width: logoHeightPx * LOGO_ASPECT, height: logoHeightPx },
      });
    } catch {
      // Logo fetch failed — proceed without it rather than blocking the export.
    }

    sheet.mergeCells(`A4:${lastColLetter}4`);
    sheet.getRow(4).height = 4; // thin spacer row

    // ── Report title (row 5) + subtitle/generated stamp (row 6) ───────────────────────────
    sheet.mergeCells(`A5:${lastColLetter}5`);
    const titleCell = sheet.getCell("A5");
    titleCell.value = title;
    titleCell.font = { bold: true, size: 13, color: { argb: "FFFFFFFF" } };
    titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
    titleCell.alignment = { vertical: "middle" };
    sheet.getRow(5).height = 22;

    sheet.mergeCells(`A6:${lastColLetter}6`);
    sheet.getCell("A6").value = [subtitle, getGeneratedStamp(generatedBy)].filter(Boolean).join("  ·  ");
    sheet.getCell("A6").font = { italic: true, size: 9, color: { argb: "FF64748B" } };

    // ── Section heading (row 7) — this sheet's leave type, distinct from the report title ──
    sheet.mergeCells(`A7:${lastColLetter}7`);
    const headingCell = sheet.getCell("A7");
    headingCell.value = section.heading;
    headingCell.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
    headingCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1B4332" } };
    headingCell.alignment = { vertical: "middle", indent: 1 };
    sheet.getRow(7).height = 20;

    sheet.addRow([]); // spacer before the data table (row 8)

    // ── Data table (headers row 9, data from row 10) ───────────────────────────────────────
    const headerRow = sheet.addRow(section.headers);
    headerRow.eachCell((cell: ExcelJS.Cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
      cell.border = {
        top: { style: "thin", color: { argb: BORDER_COLOR } },
        bottom: { style: "thin", color: { argb: BORDER_COLOR } },
        left: { style: "thin", color: { argb: BORDER_COLOR } },
        right: { style: "thin", color: { argb: BORDER_COLOR } },
      };
    });

    if (section.rows.length) {
      section.rows.forEach((row, i) => {
        const r = sheet.addRow(row);
        if (i % 2 === 1) {
          r.eachCell((cell: ExcelJS.Cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ALT_ROW_FILL } };
          });
        }
        r.eachCell((cell: ExcelJS.Cell) => {
          cell.border = {
            top: { style: "thin", color: { argb: BORDER_COLOR } },
            bottom: { style: "thin", color: { argb: BORDER_COLOR } },
            left: { style: "thin", color: { argb: BORDER_COLOR } },
            right: { style: "thin", color: { argb: BORDER_COLOR } },
          };
        });
      });
    } else {
      const r = sheet.addRow(["No employees are currently entitled to this leave type."]);
      sheet.mergeCells(`A${r.number}:${lastColLetter}${r.number}`);
      r.getCell(1).font = { italic: true, size: 10, color: { argb: "FF6B7280" } };
    }

    // Simple content-based column autofit, same approach as the flat exportToXlsx above.
    section.headers.forEach((h, i) => {
      const col = sheet.getColumn(i + 1);
      const longest = section.rows.reduce((max, row) => {
        const len = String(row[i] ?? "").length;
        return len > max ? len : max;
      }, h.length);
      col.width = Math.min(45, Math.max(12, longest + 2));
    });

    sheet.pageSetup = {
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
    };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const base = filename.replace(/\.xlsx?$/i, "");
  downloadBlob(blob, `${base}.xlsx`);
}

export async function exportToXlsx({
  title,
  headers,
  rows,
  filename,
  subtitle,
  generatedBy,
}: XlsxExportOptions): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "EATTA Electronic Management System";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet(title.slice(0, 31) || "Report");

  const colCount = Math.max(headers.length, 4);
  const lastColLetter = sheet.getColumn(colCount).letter;

  // ── Letterhead block (rows 1–4): logo top-left, company identity to its right ──────────
  const letterhead = getReportLetterhead();

  sheet.mergeCells(`B1:${lastColLetter}1`);
  sheet.getCell("B1").value = letterhead.companyName;
  sheet.getCell("B1").font = { bold: true, size: 14, color: { argb: "FF047857" } };

  sheet.mergeCells(`B2:${lastColLetter}2`);
  sheet.getCell("B2").value = letterhead.addressLine;
  sheet.getCell("B2").font = { size: 9, color: { argb: "FF64748B" } };

  sheet.mergeCells(`B3:${lastColLetter}3`);
  sheet.getCell("B3").value = letterhead.contactLine;
  sheet.getCell("B3").font = { size: 9, color: { argb: "FF64748B" } };

  try {
    const dataUrl = await getLogoDataUrl();
    const base64 = dataUrl.split(",")[1];
    const imageId = workbook.addImage({ base64, extension: "png" });
    const logoHeightRows = 4;
    const logoHeightPx = logoHeightRows * 20; // ~20px per default row height
    sheet.addImage(imageId, {
      tl: { col: 0, row: 0 },
      ext: { width: logoHeightPx * LOGO_ASPECT, height: logoHeightPx },
    });
  } catch {
    // Logo fetch failed (offline asset issue, etc.) — export still proceeds without it rather
    // than blocking the whole report.
  }

  sheet.mergeCells(`A4:${lastColLetter}4`);
  sheet.getRow(4).height = 4; // thin spacer row

  // ── Report title + subtitle/generated stamp (row 5–6) ──────────────────────────────────
  sheet.mergeCells(`A5:${lastColLetter}5`);
  const titleCell = sheet.getCell("A5");
  titleCell.value = title;
  titleCell.font = { bold: true, size: 13, color: { argb: "FFFFFFFF" } };
  titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
  titleCell.alignment = { vertical: "middle" };
  sheet.getRow(5).height = 22;

  sheet.mergeCells(`A6:${lastColLetter}6`);
  sheet.getCell("A6").value = [subtitle, getGeneratedStamp(generatedBy)].filter(Boolean).join("  ·  ");
  sheet.getCell("A6").font = { italic: true, size: 9, color: { argb: "FF64748B" } };

  sheet.addRow([]); // spacer before the data table (row 7)

  // ── Data table (headers row 8, data from row 9) ────────────────────────────────────────
  const headerRow = sheet.addRow(headers);
  headerRow.eachCell((cell: ExcelJS.Cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
    cell.border = {
      top: { style: "thin", color: { argb: BORDER_COLOR } },
      bottom: { style: "thin", color: { argb: BORDER_COLOR } },
      left: { style: "thin", color: { argb: BORDER_COLOR } },
      right: { style: "thin", color: { argb: BORDER_COLOR } },
    };
  });

  rows.forEach((row, i) => {
    const r = sheet.addRow(row);
    if (i % 2 === 1) {
      r.eachCell((cell: ExcelJS.Cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ALT_ROW_FILL } };
      });
    }
    r.eachCell((cell: ExcelJS.Cell) => {
      cell.border = {
        top: { style: "thin", color: { argb: BORDER_COLOR } },
        bottom: { style: "thin", color: { argb: BORDER_COLOR } },
        left: { style: "thin", color: { argb: BORDER_COLOR } },
        right: { style: "thin", color: { argb: BORDER_COLOR } },
      };
    });
  });

  // Simple content-based column autofit (exceljs doesn't do this automatically).
  headers.forEach((h, i) => {
    const col = sheet.getColumn(i + 1);
    const longest = rows.reduce((max, row) => {
      const len = String(row[i] ?? "").length;
      return len > max ? len : max;
    }, h.length);
    col.width = Math.min(45, Math.max(12, longest + 2));
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  // Callers migrating from the old .xls-producing downloadXls() sometimes pass a filename that
  // already ends in ".xls" (or, now, ".xlsx") — strip either before appending ".xlsx" so we
  // don't end up with a double extension like "report.xls.xlsx".
  const base = filename.replace(/\.xlsx?$/i, "");
  downloadBlob(blob, `${base}.xlsx`);
}
